/**
 * Refusal codes the CAT workspace's shared guards answer with, on every CAT workspace, reference
 * and terminology route that passes through them (task_management `error::refusal_code`). Error
 * bodies are `{ error, code, params }`, not camelized. Every environment runs a server with the
 * codes (task_management#1991, v1.131.0), so a reader matches the code alone and never the
 * English `error`.
 *
 * - `CAT_WORKFLOW_COMPLETED` (400): the job's workflow is complete and the request would change
 *   the job. `error` stays "This CAT workflow is complete; reopen it before making changes".
 * - `CAT_WORKSPACE_UNAVAILABLE` (400): no workspace of the job the caller may open, the job
 *   missing or the caller not on it (one answer for both). `error` stays "BadRequest".
 * - `CAT_JOB_BUSY` (503): another change to the job (a segment save, a bilingual import, a
 *   workflow, reference or glossary change, an external publication step) held the job's mutation
 *   lock past the 30 seconds a request waits for it. Nothing was changed, and sending the same
 *   request again once that change has finished is safe. Not an outage: the server is up and
 *   answering. A server from before it kept the request waiting instead, so it has no fallback.
 */
export const CAT_REFUSAL = {
  workflowCompleted: "CAT_WORKFLOW_COMPLETED",
  workspaceUnavailable: "CAT_WORKSPACE_UNAVAILABLE",
  jobBusy: "CAT_JOB_BUSY",
} as const

const errorResponse = (error: unknown) => {
  if (typeof error !== "object" || error === null || !("response" in error))
    return undefined
  return (error as { response?: { status?: number; data?: unknown } }).response
}

/** `503 CAT_JOB_BUSY`, read under the status it is sent with. */
export const isCatJobBusy = (error: unknown) => {
  const response = errorResponse(error)
  const data = response?.data
  return (
    response?.status === 503 &&
    typeof data === "object" &&
    data !== null &&
    (data as { code?: unknown }).code === CAT_REFUSAL.jobBusy
  )
}

/** The copy for `CAT_JOB_BUSY`, wherever a CAT change can meet it. */
export const CAT_JOB_BUSY_MESSAGE_KEY = "mt.catJobBusy"

/**
 * `key`, the caller's own copy for a failure, unless the failure is `CAT_JOB_BUSY`: then the
 * message that says the job is busy, nothing was changed, and to try again in a moment.
 */
export const catJobBusyOr = <Key extends string>(
  error: unknown,
  key: Key
): Key | typeof CAT_JOB_BUSY_MESSAGE_KEY =>
  isCatJobBusy(error) ? CAT_JOB_BUSY_MESSAGE_KEY : key
